import { useSentry } from '@/wabunka-src/common/composables/general/useSentry/useSentry';
import { useFeaturedDestinationsQuery } from '@/wabunka-src/common/features/Featured/composables/query/useFeaturedDestinationsQuery';
import { useLatestFeaturedDestinationsQuery } from '@/wabunka-src/common/features/Featured/composables/query/useLatestFeaturedDestinationsQuery';
import {
  buildSelectableDestinations,
  type CityPoint,
  type DestinationPointsJson,
  type SelectableDestination,
} from '@/wabunka-src/common/features/Featured/core/featuredDestinations/featuredDestinationsMap';

/**
 * 一覧マップのエリアラベル層（JCAT-5434）が使う「選択できる行き先」を**遅延取得**する Composable。
 * Page でのみ使用可能。
 *
 * microCMS `en-featured`(destination) に `id == 行き先タグ webPath` のエントリがある
 * 行き先タグ（都市）だけが対象になる。突合キーが
 * LP `/{webPath}` の解決キーと同じなので、カードの遷移先が必ず中身付きで開けることが保証される。
 *
 * 必要なデータは3つ（行き先タググループ RPC / microCMS featured / 静的な座標 JSON）だが、
 * エリアラベル層は地図を触らないと到達しない。一覧マップは Maps JavaScript API のロードと
 * ピン配信で既に重いため、**地図を一度も触らないユーザーには1本も投げない**。
 * 呼び出し側が最初の地図操作で `prefetch()` する（`shouldPrefetchAreaData`）。
 * ズームでは絞らない。層は狭域でも「視野に体験が無い」で入るため、絞ると視野が空になってから
 * 取得が始まり、体験が消えてからラベルが出るまでが目に見えて空く。
 *
 * 3本とも失敗しても例外にせず空で返す（graceful degradation）。エリアラベルが出ないだけで、
 * 地図は従来どおりの体験ピン／クラスタ表示のまま動く（ADR-108）。
 */
export const useExperienceMapAreaDestinations = async () => {
  // NOTE: setup コンテキスト（`inject` / `useNuxtApp`）に依存する呼び出しは、この関数の
  // **最初の await より前**にまとめて済ませる。composable 内の await を跨ぐと Vue の
  // currentInstance が失われ、以降の `inject()` が「a.inject is not a function」で落ちる
  // （＝一覧ページが SSR 500 になる）。await するものは必ず最後に置くこと。
  const { destinationContentList, hasErrored: destinationContentErrored, execute: fetchDestinationContent } = useLatestFeaturedDestinationsQuery();
  const { captureWabunkaWarning } = useSentry();
  const cityPoints = ref<CityPoint[]>([]);
  // 取得を一度だけ走らせるためのラッチ。ズーム操作のたびに prefetch が呼ばれるため必須。
  const hasStarted = ref<boolean>(false);

  // immediate: false。SSR では取らず、ズームで必要になってから取りに行く。
  const { destinationGroups, hasErrored: destinationGroupsErrored, execute: fetchDestinationGroups } = await useFeaturedDestinationsQuery({ immediate: false });

  /**
   * 都市点（行き先ごとの緯度経度・ラベル向き）を静的アセットから読む。
   *
   * SPA フォールバックで 200 + index.html が返る構成もあり得るため、ステータスだけでなく
   * 中身の形も確認してから使う。
   */
  const fetchCityPoints = async (): Promise<CityPoint[]> => {
    const res = await fetch('/featured-destinations/destinations.json');
    if (!res.ok) throw new Error(`destinations.json fetch failed: ${res.status}`);
    const json = (await res.json()) as DestinationPointsJson;
    if (!Array.isArray(json?.cities)) throw new Error('destinations.json has no cities');
    // 緯度経度は本チケットで足したフィールド。旧世代のキャッシュを掴むと欠落しており、
    // 形だけ見て通すと投影が NaN になり全ドットがオーバーレイ左上に積み上がる
    // （NaN は画面外判定の比較も全て false ですり抜ける）。値の型まで見て弾く。
    const cities = json.cities.filter((city) => Number.isFinite(city?.latitude) && Number.isFinite(city?.longitude));
    if (cities.length === 0) throw new Error('destinations.json has no city with coordinates');
    return cities;
  };

  /**
   * `useAsyncData` 由来の取得の失敗を Sentry の警告として上げる。
   *
   * `execute()` / `refresh()` は**失敗しても reject しない**（結果は status に入る）。
   * `.catch()` を付けても一度も呼ばれないため、完了後に status を見て判定する。
   */
  const warnIfAsyncDataFailed = (what: string, hasErrored: () => boolean) => () => {
    if (hasErrored()) captureWabunkaWarning(`failed to load area markers (${what})`);
  };

  /** エリアラベル層のデータを取りに行く（2回目以降は何もしない）。 */
  const prefetch = () => {
    if (hasStarted.value) return;
    hasStarted.value = true;

    // 3本は互いに独立なので待ち合わせない。揃ったものから順に地図へ反映される
    // （行き先は3本すべてが揃って初めて1件も出ないため、体験としては「出るか出ないか」だけ）。
    // NOTE: `useAsyncFetchFromPublicWabunkaApi` のエラー検査は composable 呼び出し直後の 1 回だけで、
    // `immediate: false` にしたこの経路では execute() の失敗が throw も Sentry 送出もされない。
    // 黙って空配列に落ちるとエリアラベル層が全ユーザーで一度も出ないまま無音になり、唯一の到達計測
    // （map_area_layer_enter）でも「壊れている」と「誰もズームアウトしていない」が区別できない。
    void fetchDestinationGroups().then(warnIfAsyncDataFailed('destination tag groups', () => destinationGroupsErrored.value));
    void fetchDestinationContent().then(warnIfAsyncDataFailed('microCMS featured destinations', () => destinationContentErrored.value));
    void fetchCityPoints()
      .then((cities) => {
        cityPoints.value = cities;
      })
      .catch((e: unknown) => {
        // 同一オリジンの静的アセット取得失敗。エリアラベルが出ないだけでページは壊れないが、
        // 見た目の劣化に気付けないため Sentry に警告として上げる。
        captureWabunkaWarning(`failed to load area marker city points: ${e instanceof Error ? e.message : String(e)}`);
      });
  };

  /** 選択できる行き先。3本のいずれかが未取得・失敗なら空配列になり、エリアラベルは出ない。 */
  const areaDestinations = computed<SelectableDestination[]>(() => buildSelectableDestinations(destinationGroups.value, destinationContentList.value, cityPoints.value));

  return { areaDestinations, areaCityPoints: cityPoints, prefetchAreaDestinations: prefetch };
};
